import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportFormatService } from '../../services/reportFormatService';
import { REPORT_FORMAT_DEFAULTS } from '../../types/reportFormat';
import { useNavigate, useParams } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link,
  Card,

} from '@mui/material';
import {
  Send,
  Translate,
  Print,
  ContentCopy,
  Description,
  Person,
  Receipt,
  AutoAwesome,
  Chat,
  Medication,
  LocalDining,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { consultationService, ConsultationResponse } from '../../services/consultationService';
import { templateService } from '../../services/templateService';
import { Template } from '../../types/template';
import { useConsultationStore } from '../../stores/consultationStore';
import { PageHeader } from '../../components/common/PageHeader';
import { AudioRecorder } from '../../components/consultation/AudioRecorder';
import { AmbientRecorder } from '../../components/consultation/AmbientRecorder';
import { AISuggestionsDisplay } from '../../components/consultation/AISuggestionsDisplay';
import { DrugSearch } from '../../components/consultation/DrugSearch';
import { DrugDetailsCard } from '../../components/consultation/DrugDetailsCard';
import { AlimentationCalculator } from '../../components/consultation/AlimentationCalculator';
import { CitationPopup } from '../../components/common/CitationPopup';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: any[];
}

export function ConsultationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { consultationId } = useParams<{ consultationId: string }>();
  const setActiveConsultation = useConsultationStore((state) => state.setActiveConsultation);
  const clearActiveConsultation = useConsultationStore((state) => state.clearActiveConsultation);

  // State
  const [consultation, setConsultation] = useState<ConsultationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [reportText, setReportText] = useState('');
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatQuestion, setChatQuestion] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false); // True once first token arrives

  // Report format settings (for print)
  const { data: clientReportFormat } = useQuery({
    queryKey: ['reportFormat', 'client_report'],
    queryFn: () => reportFormatService.getFormat('client_report'),
    retry: false,
    throwOnError: false,
  });

  // Report tabs state
  const [reportTab, setReportTab] = useState(0); // 0 = compte-rendu, 1 = client report, 2 = prescription
  const [aiTab, setAiTab] = useState(0); // 0 = suggestions/chat, 1 = drugs
  const [clientReport, setClientReport] = useState('');
  const [reportLanguage, setReportLanguage] = useState<'fr' | 'en'>('fr');
  const [generatingClientReport, setGeneratingClientReport] = useState(false);
  const [prescription, setPrescription] = useState('');
  const [generatingPrescription, setGeneratingPrescription] = useState(false);
  const [sendingClientReportEmail, setSendingClientReportEmail] = useState(false);
  const [sendingPrescriptionEmail, setSendingPrescriptionEmail] = useState(false);
  const [clientReportEmail, setClientReportEmail] = useState('');
  const [prescriptionEmail, setPrescriptionEmail] = useState('');

  // Animal info state
  const [animalInfoDraft, setAnimalInfoDraft] = useState({
    patient_name: '',
    patient_species: '',
    patient_breed: '',
    patient_age: '',
    patient_gender: '',
    sterilization_status: '',
    patient_weight: null as number | null,
  });

  // Drug-related state
  const [extractedDrugs, setExtractedDrugs] = useState<string[]>([]);
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  const [drugMoleculeQuery, setDrugMoleculeQuery] = useState<string | undefined>(undefined);

  // Audio recorder reset trigger - incremented to flush audio when consultation changes
  const [audioResetTrigger, setAudioResetTrigger] = useState(0);

  // Template state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [lastAppliedTemplate, setLastAppliedTemplate] = useState<string>(''); // Track last template content for smart switching

  // Citation popup state
  const [chatCitationAnchorEl, setChatCitationAnchorEl] = useState<HTMLElement | null>(null);
  const [activeChatCitation, setActiveChatCitation] = useState<any | null>(null);

  // Detect if running in extension mode (iframe)
  const isExtensionMode = window.self !== window.top;

  const handleChatCitationClick = (event: React.MouseEvent<HTMLElement>, citation: any) => {
    setChatCitationAnchorEl(event.currentTarget);
    setActiveChatCitation(citation);
  };

  const handleCloseChatCitation = () => {
    setChatCitationAnchorEl(null);
    setActiveChatCitation(null);
  };

  const handleNavigateToDrug = (drugId: string) => {
    setAiTab(2);
    setSelectedDrugId(drugId);
  };

  const handleSearchByMolecule = (molecule: string) => {
    setAiTab(2);
    setDrugMoleculeQuery(molecule);
    // Reset after a tick so re-triggering the same molecule works
    setTimeout(() => setDrugMoleculeQuery(undefined), 500);
  };

  const chatEndRef = useRef<HTMLDivElement>(null);

  // Helper to request scraped context from the Chrome Extension
  const requestScraperContext = () => {
    const inIframe = window.self !== window.top;
    console.log("[DEBUG frontend] requestScraperContext() called — inIframe=" + inIframe + " window.parent=" + (window.parent === window ? "SAME (not in iframe!)" : "different (OK)"));
    window.parent.postMessage({ type: 'REFRESH_CONTEXT' }, '*');
  };

  // Keep ref for fresh state access in event listener without triggering rebuilds
  const animalInfoRef = useRef(animalInfoDraft);
  useEffect(() => {
    animalInfoRef.current = animalInfoDraft;
  }, [animalInfoDraft]);

  // Tracks the consultation ID that is currently loaded in the UI.
  // Used to discard async results (transcription, suggestions) that completed
  // after the user navigated to a different consultation.
  const currentConsultationIdRef = useRef<string | null>(null);

  // Listen for scraped data from Chrome Extension Side Panel
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data && event.data.type === 'VETAI_INJECT_CONTEXT') {
        const payload = event.data.payload;
        console.log("🔌 [VetAI] Données reçues du Scraper :", payload);
        if (payload) {
          // If the payload has no meaningful patient data (name, age or weight),
          // it means GmVET didn't have a patient open → reset the form.
          // This prevents stale data from a previous consultation bleeding through
          // when the merge logic falls back to currentInfo for all empty fields.
          const hasPatientData = !!(payload.patient_name || payload.patient_age || payload.patient_weight);
          if (!hasPatientData) {
            console.log("🔌 [VetAI] Payload vide — réinitialisation des infos animal.");
            setAnimalInfoDraft({
              patient_name: '',
              patient_species: '',
              patient_breed: '',
              patient_age: '',
              patient_gender: '',
              sterilization_status: '',
              patient_weight: null,
            });
            return;
          }

          // Priority: scraped data (payload) > existing draft (currentInfo)
          // Scraped data reflects the current GmVET patient, so it always wins.
          // Existing draft is only a fallback for fields the scraper couldn't read.
          const currentInfo = animalInfoRef.current;
          const mergedData = {
            patient_name: payload.patient_name || currentInfo.patient_name,
            patient_species: payload.patient_species || currentInfo.patient_species,
            patient_breed: payload.patient_breed || currentInfo.patient_breed,
            patient_age: payload.patient_age || currentInfo.patient_age,
            patient_gender: payload.patient_gender || currentInfo.patient_gender,
            sterilization_status: payload.sterilization_status || currentInfo.sterilization_status,
            patient_weight: (payload.patient_weight ? parseFloat(payload.patient_weight.toString().replace(',','.')) : null) ?? currentInfo.patient_weight,
          };

          setAnimalInfoDraft(mergedData);

          // Auto-save scraped data to DB immediately if consultation exists
          // Use consultationId from router params to guarantee fresh value and avoid closure trap
          if (consultationId) {
            console.log("💾 [VetAI] Auto-sauvegarde des données scrapées vers la DB...");
            try {
              await consultationService.updateConsultation({
                consultation_id: consultationId,
                ...mergedData,
              });
              console.log("✅ [VetAI] Données scrapées sauvegardées avec succès");

              // Update local consultation state using functional update to ensure latest state is used
              setConsultation(prev => {
                if (!prev) return null;
                return {
                  ...prev,
                  ...mergedData,
                };
              });
            } catch (err) {
              console.error("❌ [VetAI] Erreur lors de la sauvegarde des données scrapées:", err);
            }
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [consultationId]);

  // Load consultation if consultationId is provided
  useEffect(() => {
    if (!consultationId) {
      return;
    }

    const loadConsultation = async () => {
      // Immediately claim this consultation as active so any in-flight async operations
      // from the previous consultation detect the change and discard their results.
      currentConsultationIdRef.current = consultationId;

      // Reset stale loading/error states that may have been left by the previous consultation.
      setGeneratingSuggestions(false);
      setError(null);

      // The route reuses this component between consultations. Clear template state
      // immediately so the previous consultation's selection cannot remain visible.
      setSelectedTemplateId('');
      setLastAppliedTemplate('');

      // Reset animal info immediately so stale data from the previous consultation
      // is never carried over via the `prev` fallback in the functional update below.
      // Scraped data arriving before the API call will still win (animalInfoRef tracks this).
      setAnimalInfoDraft({
        patient_name: '',
        patient_species: '',
        patient_breed: '',
        patient_age: '',
        patient_gender: '',
        sterilization_status: '',
        patient_weight: null,
      });

      try {
        setLoading(true);
        const consultationData = await consultationService.getById(consultationId);
        setConsultation(consultationData);
        setSelectedTemplateId(consultationData.template_id || '');
        const initialReportText = consultationData.corrected_transcription || '';
        setReportText(initialReportText);

        // Don't set lastAppliedTemplate here - will be done in a separate effect
        // after templates are loaded

        setClientReport(consultationData.client_report || '');
        setPrescription(consultationData.prescription || '');

        // Initialize animal info draft — DB data wins if present, otherwise keep
        // any already-populated scraped data (scraping can arrive before this API call completes)
        setAnimalInfoDraft(prev => ({
          patient_name: consultationData.patient_name || prev.patient_name || '',
          patient_species: consultationData.patient_species || prev.patient_species || '',
          patient_breed: consultationData.patient_breed || prev.patient_breed || '',
          patient_age: consultationData.patient_age || prev.patient_age || '',
          patient_gender: consultationData.patient_gender || prev.patient_gender || '',
          sterilization_status: consultationData.sterilization_status || prev.sterilization_status || '',
          patient_weight: consultationData.patient_weight ?? prev.patient_weight ?? null,
        }));

        // Trigger scraping for brand-new consultations with no patient data yet
        if (consultationData.status === 'IN_PROGRESS' && !consultationData.patient_name && !consultationData.patient_species) {
          requestScraperContext();
        }

        // Set active consultation in store
        setActiveConsultation({
          consultationId: consultationData.id,
          patientName: consultationData.patient_name || 'Unnamed',
          ownerName: consultationData.client_name || '',
        });

        // Load chat messages if available, otherwise clear them
        if (consultationData.chat_messages && consultationData.chat_messages.length > 0) {
          const parsedMessages = consultationData.chat_messages.map((msg: any) => {
            // Check for citations block in content
            if (msg.content && typeof msg.content === 'string' && msg.content.includes('---CITATIONS---')) {
              const parts = msg.content.split('---CITATIONS---');
              const content = parts[0].trim();
              let citations = [];
              try {
                citations = JSON.parse(parts[1].trim());
              } catch (e) {
                console.error('Failed to parse citations from history', e);
              }
              return { ...msg, content, citations };
            }
            return msg;
          });
          setChatMessages(parsedMessages);
        } else {
          setChatMessages([]);
        }

        // Load extracted drugs if available
        if (consultationData.extracted_drugs && Array.isArray(consultationData.extracted_drugs)) {
          // Handle both string arrays and object arrays (if backend returns objects)
          const drugNames = consultationData.extracted_drugs.map((drug: any) => drug.name || drug).filter(Boolean);
          setExtractedDrugs(drugNames);
        }

        // Reset audio recorder when consultation changes
        setAudioResetTrigger(prev => prev + 1);
      } catch (err) {
        console.error('Failed to load consultation:', err);
        setError(t('consultation.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    loadConsultation();
  }, [consultationId, setActiveConsultation, t]);

  // Fetch templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const fetchedTemplates = await templateService.getAllTemplates();
        setTemplates(fetchedTemplates);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };

    fetchTemplates();
  }, []);

  // Check if reportText matches a template after templates are loaded
  // This handles new consultations that are pre-filled with default templates
  useEffect(() => {
    if (templates.length === 0 || !reportText || lastAppliedTemplate) {
      return; // Skip if templates not loaded, no text, or already tracked
    }

    const matchingTemplate = templates.find(t => t.content === reportText);
    if (matchingTemplate) {
      setLastAppliedTemplate(reportText);
    }
  }, [templates, reportText, lastAppliedTemplate]);

  // Start new consultation
  const handleStartSession = async () => {
    setLoading(true);
    try {
      // Check if template prefill is enabled (default to true)
      const prefillEnabled = localStorage.getItem('template_prefill_enabled') !== 'false';

      // Create the consultation first (without animal data — scraping happens after navigation)
      const newConsultation = await consultationService.startConsultation({
        prefill_with_template: prefillEnabled,
      });

      // Fire scraping request NOW — by the time the extension responds (~100ms+),
      // the new component at /consultation/:id will be mounted and its
      // VETAI_INJECT_CONTEXT listener will be active with the correct consultationId.
      requestScraperContext();

      navigate(`/consultation/${newConsultation.id}`);
    } catch (err) {
      console.error('Failed to start session:', err);
      setError('Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  // Handle recording complete
  const handleRecordingComplete = async (audioBlob: Blob) => {
    if (!consultation) {
      setError(t('consultation.notStarted'));
      return;
    }

    const capturedId = consultation.id;

    try {

      setLoading(true);
      setError(null);

      // Upload audio
      await consultationService.uploadAudio({
        consultation_id: capturedId,
        audio_file: audioBlob,
      });

      // Trigger transcription
      const transcribedConsultation = await consultationService.transcribe({
        consultation_id: capturedId,
        existing_report: reportText, // Pass the current text in the editor
      });

      // Consultation changed while we were waiting: the backend already saved the result
      // for the original consultation. Do not corrupt the currently displayed consultation.
      if (currentConsultationIdRef.current !== capturedId) {
        return;
      }

      setConsultation(transcribedConsultation);

      if (transcribedConsultation.corrected_transcription) {
        setReportText(transcribedConsultation.corrected_transcription);
        // Clear last applied template since text has been modified by transcription
        setLastAppliedTemplate('');
      }

      // Update animal info if returned
      if (transcribedConsultation.patient_name || transcribedConsultation.patient_species || transcribedConsultation.patient_weight || transcribedConsultation.patient_breed || transcribedConsultation.patient_age || transcribedConsultation.patient_gender || transcribedConsultation.sterilization_status) {
        setAnimalInfoDraft(prev => ({
          ...prev,
          // Update animal info if returned - merge with previous but prioritize new non-empty
          patient_name: transcribedConsultation.patient_name || prev.patient_name,
          patient_species: transcribedConsultation.patient_species || prev.patient_species,
          patient_breed: transcribedConsultation.patient_breed || prev.patient_breed,
          patient_age: transcribedConsultation.patient_age || prev.patient_age,
          patient_gender: transcribedConsultation.patient_gender || prev.patient_gender,
          sterilization_status: transcribedConsultation.sterilization_status || prev.sterilization_status,
          patient_weight: transcribedConsultation.patient_weight || prev.patient_weight,
        }));

        // Also update the main consultation object
        setConsultation(prev => prev ? ({
          ...prev,
          patient_name: transcribedConsultation.patient_name || prev.patient_name,
          patient_species: transcribedConsultation.patient_species || prev.patient_species,
          patient_breed: transcribedConsultation.patient_breed || prev.patient_breed,
          patient_age: transcribedConsultation.patient_age || prev.patient_age,
          patient_gender: transcribedConsultation.patient_gender || prev.patient_gender,
          sterilization_status: transcribedConsultation.sterilization_status || prev.sterilization_status,
          patient_weight: transcribedConsultation.patient_weight || prev.patient_weight,
        }) : null);
      }

      // Extract drugs if returned
      if (transcribedConsultation.extracted_drugs && Array.isArray(transcribedConsultation.extracted_drugs)) {
        const drugNames = transcribedConsultation.extracted_drugs.map((drug: any) => drug.name || drug).filter(Boolean);
        setExtractedDrugs(drugNames);
      }

      setLoading(false);

      // Auto-generate AI suggestions after transcription completes
      if (transcribedConsultation.corrected_transcription) {
        triggerAutoSuggestions(capturedId, transcribedConsultation.corrected_transcription);
      }
    } catch (err: any) {
      if (err.response?.status === 413) {
        setError(t('consultation.audioFileTooLarge'));
      } else {
        setError(err.response?.data?.detail || t('consultation.audioProcessingError'));
      }
      setLoading(false);
    }
  };

  // Silently generate AI suggestions with given text (does not touch loading state)
  const triggerAutoSuggestions = async (consultationId: string, text: string) => {
    if (!text.trim()) return;
    try {
      setGeneratingSuggestions(true);
      const suggestionsResponse = await consultationService.generateSuggestions({
        consultation_id: consultationId,
        custom_text: text,
      });
      // Only apply results to the consultation that initiated the request
      setConsultation((prev) =>
        prev && prev.id === consultationId
          ? { ...prev, ai_suggestions: suggestionsResponse.ai_suggestions }
          : prev
      );
      if (currentConsultationIdRef.current === consultationId) {
        setChatMessages([]);
      }
    } catch {
      // Auto-suggestions failing is non-critical — user can still click the button manually
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  // Handle incremental report update from ambient recording
  const handleAmbientReportUpdate = (updatedReport: string, extractedInfo: any) => {
    if (updatedReport) {
      setReportText(updatedReport);
      setLastAppliedTemplate('');
    }

    if (extractedInfo) {
      setAnimalInfoDraft((prev) => ({
        ...prev,
        patient_name: extractedInfo.name || prev.patient_name,
        patient_species: extractedInfo.species || prev.patient_species,
        patient_breed: extractedInfo.breed || prev.patient_breed,
        patient_age: extractedInfo.age || prev.patient_age,
        patient_gender: extractedInfo.sex || prev.patient_gender,
        sterilization_status: extractedInfo.sterilization_status || prev.sterilization_status,
        patient_weight: extractedInfo.weight ? Number(extractedInfo.weight) : prev.patient_weight,
      }));

      setConsultation((prev) =>
        prev
          ? {
              ...prev,
              patient_name: extractedInfo.name || prev.patient_name,
              patient_species: extractedInfo.species || prev.patient_species,
              patient_breed: extractedInfo.breed || prev.patient_breed,
              patient_age: extractedInfo.age || prev.patient_age,
              patient_gender: extractedInfo.sex || prev.patient_gender,
              sterilization_status: extractedInfo.sterilization_status || prev.sterilization_status,
              patient_weight: extractedInfo.weight ? Number(extractedInfo.weight) : prev.patient_weight,
            }
          : null
      );

      if (Array.isArray(extractedInfo.drugs) && extractedInfo.drugs.length > 0) {
        const newNames = extractedInfo.drugs.map((d: any) => d.name || d).filter(Boolean);
        setExtractedDrugs((prev) => {
          const existing = new Set(prev.map((n: string) => n.toLowerCase()));
          return [...prev, ...newNames.filter((n: string) => !existing.has(n.toLowerCase()))];
        });
      }
    }
  };

  // Called when the ambient recording session fully ends (after last chunk is processed)
  const handleAmbientSessionComplete = (finalReport: string) => {
    if (consultation) {
      triggerAutoSuggestions(consultation.id, finalReport);
    }
  };

  // Generate AI suggestions
  const handleGenerateSuggestions = async () => {
    if (!consultation) return;

    const capturedId = consultation.id;

    try {
      setGeneratingSuggestions(true);
      setError(null);

      const suggestionsResponse = await consultationService.generateSuggestions({
        consultation_id: capturedId,
        custom_text: reportText,
      });

      // Only apply results to the consultation that initiated the request
      setConsultation((prev) =>
        prev && prev.id === capturedId
          ? { ...prev, ai_suggestions: suggestionsResponse.ai_suggestions }
          : prev
      );
      if (currentConsultationIdRef.current === capturedId) {
        setChatMessages([]);
      }
    } catch (err: any) {
      if (currentConsultationIdRef.current === capturedId) {
        setError(err.response?.data?.detail || t('consultation.aiSuggestionsError'));
      }
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (aiTab === 1 && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, sendingMessage, aiTab]);

  // Send chat message
  const handleSendChatMessage = async () => {
    if (!chatQuestion.trim() || !consultation) return;

    try {
      setSendingMessage(true);

      const userMessage: ChatMessage = {
        role: 'user',
        content: chatQuestion
      };
      setChatMessages(prev => [...prev, userMessage]);
      setChatQuestion('');

      // Add placeholder assistant message
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: '',
        citations: []
      };
      setChatMessages(prev => [...prev, assistantMessage]);

      await consultationService.askFollowupQuestionStream({
        consultation_id: consultation.id,
        question: userMessage.content,
        onToken: (token) => {
          console.log(`[${new Date().toISOString()}] TOKEN RECEIVED:`, token.substring(0, 50));
          setIsStreaming(true); // First token arrived, hide thinking indicator
          setChatMessages(prev => {
            const newHistory = [...prev];
            const lastIndex = newHistory.length - 1;
            const lastMsg = newHistory[lastIndex];
            if (lastMsg.role === 'assistant') {
              // Create a NEW object to trigger React re-render
              newHistory[lastIndex] = {
                ...lastMsg,
                content: lastMsg.content + token
              };
            }
            return newHistory;
          });
        },
        onCitations: (citations) => {
          console.log(`[${new Date().toISOString()}] CITATIONS:`, citations.length);
          setChatMessages(prev => {
            const newHistory = [...prev];
            const lastIndex = newHistory.length - 1;
            const lastMsg = newHistory[lastIndex];
            if (lastMsg.role === 'assistant') {
              // Create a NEW object to trigger React re-render
              newHistory[lastIndex] = {
                ...lastMsg,
                citations: citations
              };
            }
            return newHistory;
          });
        },
        onStatus: (status) => {
          console.log(`[${new Date().toISOString()}] STATUS:`, status);
        },
        onDone: () => {
          console.log(`[${new Date().toISOString()}] DONE`);
          setSendingMessage(false);
          setIsStreaming(false);
        },
        onError: (error) => {
          console.error(`[${new Date().toISOString()}] ERROR:`, error);
          setError(error);
          setSendingMessage(false);
          setIsStreaming(false);
        }
      });

    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.response?.data?.detail || err.message || t('consultation.questionSendError'));
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setSendingMessage(false);
    }
  };

  // Generate client report
  const handleGenerateClientReport = async () => {
    if (!consultation || !reportText) {
      setError(t('consultation.enterReportFirst'));
      return;
    }

    try {
      setGeneratingClientReport(true);
      setError(null);

      const response = await consultationService.generateClientReport({
        consultation_id: consultation.id,
        compte_rendu: reportText,
        language: reportLanguage,
      });

      setClientReport(response.client_report);
      setReportTab(1);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('consultation.clientReportError'));
    } finally {
      setGeneratingClientReport(false);
    }
  };

  // Animal info handlers
  const handleAnimalInfoChange = (field: string, value: string) => {
    setAnimalInfoDraft(prev => ({
      ...prev,
      [field]: field === 'patient_weight' ? (value ? parseFloat(value) : null) : value
    }));
  };

  const handleSaveAnimalInfo = async () => {
    if (!consultation) return;

    try {
      await consultationService.updateConsultation({
        consultation_id: consultation.id,
        ...animalInfoDraft,
      });

      // Update local state
      setConsultation({
        ...consultation,
        ...animalInfoDraft,
      });
    } catch (err) {
      console.error('Failed to update animal info:', err);
      setError(t('consultation.updateAnimalInfoError'));
    }
  };

  const handleSaveReport = async () => {
    if (!consultation) return;

    try {
      await consultationService.updateConsultation({
        consultation_id: consultation.id,
        corrected_transcription: reportText,
      });

      // Update local consultation state
      setConsultation({
        ...consultation,
        corrected_transcription: reportText,
      });
    } catch (err) {
      console.error('Failed to save report:', err);
    }
  };

  const handleSaveClientReport = async () => {
    if (!consultation) return;

    try {
      await consultationService.updateConsultation({
        consultation_id: consultation.id,
        client_report: clientReport,
      });

      // Update local consultation state
      setConsultation({
        ...consultation,
        client_report: clientReport,
      });
    } catch (err) {
      console.error('Failed to save client report:', err);
    }
  };

  const handleSavePrescription = async () => {
    if (!consultation) return;

    try {
      await consultationService.updateConsultation({
        consultation_id: consultation.id,
        prescription: prescription,
      });

      // Update local consultation state
      setConsultation({
        ...consultation,
        prescription: prescription,
      });
    } catch (err) {
      console.error('Failed to save prescription:', err);
    }
  };

  // Print handlers
  const buildReportPrintHtml = (title: string, bodyHtml: string): string => {
    const fmt = clientReportFormat;
    const defaults = REPORT_FORMAT_DEFAULTS.client_report;
    const accent = fmt?.accent_color || defaults.accent_color || '#1565c0';
    const font = fmt?.font_family || defaults.font_family || "'Segoe UI', sans-serif";
    const headerRich = fmt?.header_text || '';
    const headerImage = fmt?.header_image || null;
    const footerText = fmt?.footer_text || '';
    const customClosing = fmt?.custom_closing || '';

    const headerSection = (headerRich || headerImage)
      ? `<div class="doc-header">
           ${headerImage ? `<img src="${headerImage}" class="header-logo" alt="logo"/>` : ''}
           ${headerRich ? `<div class="header-rich">${headerRich}</div>` : ''}
         </div>`
      : '';

    const footerSection = footerText
      ? `<div class="doc-footer">${footerText.replace(/\n/g, '<br/>')}</div>`
      : '';

    const closingSection = customClosing
      ? `<div class="doc-closing">${customClosing.replace(/\n/g, '<br/>')}</div>`
      : '';

    return `<!DOCTYPE html><html><head>
      <title>${title}</title>
      <style>
        @page { size: auto; margin: 0; }
        body { font-family: ${font}, sans-serif; padding: 14mm 16mm; line-height: 1.6; color: #333; font-size: 13px; }
        .doc-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 12px; border-bottom: 2px solid ${accent}; margin-bottom: 18px; }
        .header-logo { max-height: 70px; max-width: 140px; object-fit: contain; flex-shrink: 0; }
        .header-rich { font-size: 10px; flex: 1; min-width: 0; }
        .header-rich p { margin: 0; }
        h1 { color: ${accent}; border-bottom: 2px solid ${accent}; padding-bottom: 8px; margin-bottom: 18px; font-size: 18px; }
        h2 { color: ${accent}; margin-top: 18px; border-bottom: 1px solid #eee; padding-bottom: 4px; font-size: 15px; }
        h3 { color: #34495e; margin-top: 14px; font-size: 13px; }
        strong { color: #2c3e50; }
        ul, ol { padding-left: 20px; margin-bottom: 12px; }
        li { margin-bottom: 4px; }
        p { margin-bottom: 12px; }
        .doc-closing { margin-top: 24px; font-style: italic; color: #555; font-size: 12px; }
        .doc-footer { margin-top: 28px; padding-top: 8px; border-top: 1px solid #ddd; font-size: 9px; color: #888; }
        @media print { a { text-decoration: none; color: inherit; } }
      </style>
    </head><body>
      ${headerSection}
      <h1>${title}</h1>
      <div>${bodyHtml}</div>
      ${closingSection}
      ${footerSection}
    </body></html>`;
  };

  const handlePrintClientReport = () => {
    if (!clientReport) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const bodyHtml = renderToStaticMarkup(<ReactMarkdown>{clientReport}</ReactMarkdown>);
    printWindow.document.write(buildReportPrintHtml('Compte rendu de consultation', bodyHtml));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePrintPrescription = () => {
    if (!prescription) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const bodyHtml = renderToStaticMarkup(<ReactMarkdown>{prescription}</ReactMarkdown>);
    printWindow.document.write(buildReportPrintHtml('Ordonnance vétérinaire', bodyHtml));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };


  // Handle template selection change
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);

    // Persist the selected template to the backend so transcription uses the right structure
    if (consultation) {
      consultationService.updateConsultation({
        consultation_id: consultation.id,
        template_id: templateId || null,
      }).catch(err => console.error('Failed to save template_id:', err));
    }

    // If no template selected (empty string), don't apply anything
    if (!templateId) {
      return;
    }

    // Find the selected template
    const template = templates.find(t => t.id === templateId);
    if (!template) {
      return;
    }

    // Only apply template if:
    // 1. Report text is empty, OR
    // 2. Report text exactly matches the last applied template (unmodified)
    const shouldApplyTemplate =
      reportText.trim() === '' ||
      reportText === lastAppliedTemplate;

    if (shouldApplyTemplate) {
      setReportText(template.content);
      setLastAppliedTemplate(template.content);
    }
  };

  // Email handlers
  const handleSendClientReportEmail = async () => {
    if (!consultation || !clientReport) {
      setError(t('consultation.noReportToSend'));
      return;
    }

    if (!clientReportEmail || !clientReportEmail.trim()) {
      setError(t('consultation.clientNoEmail'));
      return;
    }

    try {
      setSendingClientReportEmail(true);
      setError(null);
      setSuccess(null);

      await consultationService.sendClientReportEmail(consultation.id, clientReportEmail);
      setSuccess(t('consultation.emailSentSuccess'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('consultation.emailSendError'));
    } finally {
      setSendingClientReportEmail(false);
    }
  };

  const handleSendPrescriptionEmail = async () => {
    if (!consultation || !prescription) {
      setError(t('consultation.noPrescriptionToSend'));
      return;
    }

    if (!prescriptionEmail || !prescriptionEmail.trim()) {
      setError(t('consultation.clientNoEmail'));
      return;
    }

    try {
      setSendingPrescriptionEmail(true);
      setError(null);
      setSuccess(null);

      await consultationService.sendPrescriptionEmail(consultation.id, prescriptionEmail);
      setSuccess(t('consultation.emailSentSuccess'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('consultation.emailSendError'));
    } finally {
      setSendingPrescriptionEmail(false);
    }
  };

  // Render: Start consultation screen
  if (!consultation) {
    return (
      <Box>
        <PageHeader
          title={t('consultation.title')}
          subtitle={t('consultation.newSession')}
          breadcrumbs={[
            { label: t('navigation.home'), path: '/dashboard' },
            { label: t('navigation.consultations') },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Card sx={{ maxWidth: 400, p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              {t('consultation.startNewSession')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Click the button below to begin a new consultation session
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleStartSession}
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Start New Consultation'}
            </Button>
          </Card>
        </Box>
      </Box>
    );
  }

  // Render: Active consultation
  return (
    <Box>
      <PageHeader
        title={t('consultation.title')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }}>

      {/* Animal Information Card */}
      <Grid item xs={12}>
      <Card sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">
            {t('consultation.animalInfo')}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              label={t('consultation.animalName')}
              value={animalInfoDraft.patient_name}
              onChange={(e) => handleAnimalInfoChange('patient_name', e.target.value)}
              onBlur={handleSaveAnimalInfo}
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('consultation.species')}</InputLabel>
              <Select
                value={animalInfoDraft.patient_species || ''}
                onChange={(e) => handleAnimalInfoChange('patient_species', e.target.value)}
                onBlur={handleSaveAnimalInfo}
                label={t('consultation.species')}
              >
                <MenuItem value="">{t('consultation.speciesUnknown')}</MenuItem>
                <MenuItem value="chien">{t('consultation.speciesDog')}</MenuItem>
                <MenuItem value="chat">{t('consultation.speciesCat')}</MenuItem>
                <MenuItem value="oiseau">{t('consultation.speciesBird')}</MenuItem>
                <MenuItem value="lapin">{t('consultation.speciesRabbit')}</MenuItem>
                <MenuItem value="rongeur">{t('consultation.speciesRodent')}</MenuItem>
                <MenuItem value="reptile">{t('consultation.speciesReptile')}</MenuItem>
                <MenuItem value="ferme">{t('consultation.speciesFarm')}</MenuItem>
                <MenuItem value="autre">{t('consultation.speciesOther')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              label={t('consultation.breed') || "Race"}
              value={animalInfoDraft.patient_breed}
              onChange={(e) => handleAnimalInfoChange('patient_breed', e.target.value)}
              onBlur={handleSaveAnimalInfo}
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label={t('consultation.weight')}
              value={animalInfoDraft.patient_weight || ''}
              onChange={(e) => handleAnimalInfoChange('patient_weight', e.target.value)}
              onBlur={handleSaveAnimalInfo}
              type="number"
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('consultation.gender') || "Sexe"}</InputLabel>
              <Select
                value={animalInfoDraft.patient_gender || ''}
                onChange={(e) => handleAnimalInfoChange('patient_gender', e.target.value)}
                onBlur={handleSaveAnimalInfo}
                label={t('consultation.gender') || "Sexe"}
              >
                <MenuItem value="">{t('common.none') || "Non spécifié"}</MenuItem>
                <MenuItem value="Male">{t('animals.gender.male') || "Mâle"}</MenuItem>
                <MenuItem value="Female">{t('animals.gender.female') || "Femelle"}</MenuItem>
                <MenuItem value="Unknown">{t('animals.gender.unknown') || "Inconnu"}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label={t('consultation.age') || "Age"}
              value={animalInfoDraft.patient_age}
              onChange={(e) => handleAnimalInfoChange('patient_age', e.target.value)}
              onBlur={handleSaveAnimalInfo}
              size="small"
              placeholder="Ex: 5 ans"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('consultation.sterilization') || "Stérilisation"}</InputLabel>
              <Select
                value={animalInfoDraft.sterilization_status || ''}
                onChange={(e) => handleAnimalInfoChange('sterilization_status', e.target.value)}
                onBlur={handleSaveAnimalInfo}
                label={t('consultation.sterilization') || "Stérilisation"}
              >
                <MenuItem value="">Non spécifié</MenuItem>
                <MenuItem value="Stérilisé">Stérilisé</MenuItem>
                <MenuItem value="Non stérilisé">Non stérilisé</MenuItem>
                <MenuItem value="Inconnu">Inconnu</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>
      </Grid>

        {/* Left: Report Editor */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            p: { xs: 1.5, md: 3 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={reportTab}
                onChange={(_, newValue) => setReportTab(newValue)}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 0,
                    px: { xs: 1, md: 2 },
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                  }
                }}
              >
                <Tab icon={<Description />} iconPosition="start" label={t('consultation.reportTab')} />
                <Tab icon={<Person />} iconPosition="start" label={t('consultation.clientReportTab')} />
                <Tab icon={<Receipt />} iconPosition="start" label={t('consultation.prescriptionTab')} />
              </Tabs>
            </Box>

            <Box sx={{ height: { xs: '500px', md: '700px' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Tab 0: Compte-rendu */}
              {reportTab === 0 && (
                <>
                  {/* Recording tools — side by side */}
                  <Box sx={{ mb: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'row', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AudioRecorder
                        onRecordingComplete={handleRecordingComplete}
                        compact={true}
                        resetTrigger={audioResetTrigger}
                        label={t('consultation.dictateButton')}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AmbientRecorder
                        consultationId={consultation?.id}
                        existingReport={reportText}
                        language="fr"
                        onReportUpdate={handleAmbientReportUpdate}
                        onSessionComplete={handleAmbientSessionComplete}
                        disabled={loading}
                        resetTrigger={audioResetTrigger}
                      />
                    </Box>
                  </Box>

                  {/* Template selector */}
                  <Box sx={{ mb: { xs: 1, md: 2 } }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{t('consultation.template')}</InputLabel>
                      <Select
                        value={selectedTemplateId}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        label={t('consultation.template')}
                      >
                        <MenuItem value="">{t('consultation.noTemplate')}</MenuItem>
                        {templates.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name}
                            {template.is_default && ` (${t('consultation.default')})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ position: 'relative', flex: 1, mb: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column' }}>
                    {loading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          zIndex: 1,
                          borderRadius: 1,
                        }}
                      >
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          {t('consultation.processingInProgress')}
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      minRows={10}
                      value={reportText}
                      onChange={(e) => setReportText(e.target.value)}
                      onBlur={handleSaveReport}
                      placeholder={t('consultation.reportPlaceholder')}
                      sx={{
                        flex: 1,
                        mb: { xs: 1, md: 2 },
                        '& .MuiInputBase-root': {
                          height: '100%',
                          alignItems: 'flex-start'
                        },
                        '& textarea': {
                          height: '100% !important',
                          overflow: 'auto !important'
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    {/* Bouton visible uniquement dans l'extension Chrome (iframe) */}
                    {window.self !== window.top && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<Send sx={{ transform: 'rotate(180deg)' }} />}
                        onClick={() => {
                          window.parent.postMessage({
                            type: 'SEND_REPORT_TO_GMVET',
                            text: reportText,
                          }, '*');
                        }}
                        disabled={!reportText}
                      >
                        Envoyer vers GmVET
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={() => {
                        navigator.clipboard.writeText(reportText);
                      }}
                      disabled={!reportText}
                    >
                      {t('consultation.copyToClipboard')}
                    </Button>
                  </Box>
                </>
              )}

              {/* Tab 1: Client Report */}
              {reportTab === 1 && (
                <>
                  <Box sx={{ mb: { xs: 1, md: 2 }, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>{t('consultation.language')}</InputLabel>
                      <Select
                        value={reportLanguage}
                        onChange={(e) => setReportLanguage(e.target.value as 'fr' | 'en')}
                        label={t('consultation.language')}
                      >
                        <MenuItem value="fr">Français</MenuItem>
                        <MenuItem value="en">English</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Translate />}
                      onClick={handleGenerateClientReport}
                      disabled={generatingClientReport || !reportText}
                      sx={{ padding: '10px 16px', minHeight: '25px', transform: 'translateY(-3px)' }}
                    >
                      {generatingClientReport ? <CircularProgress size={20} /> : t('consultation.generateClientReport')}
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    value={clientReport}
                    onChange={(e) => setClientReport(e.target.value)}
                    onBlur={handleSaveClientReport}
                    placeholder={t('consultation.clientReportPlaceholder')}
                    sx={{
                      flex: 1,
                      mb: { xs: 1, md: 2 },
                      '& .MuiInputBase-root': {
                        height: '100%',
                        alignItems: 'flex-start',
                        fontSize: isExtensionMode ? '0.875rem' : '1rem'
                      },
                      '& textarea': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        fontSize: isExtensionMode ? '0.8125rem' : '0.875rem'
                      }
                    }}
                  />

                  <Box sx={{ mb: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('consultation.recipientEmail')}
                      value={clientReportEmail}
                      onChange={(e) => setClientReportEmail(e.target.value)}
                      type="email"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={handleSendClientReportEmail}
                      disabled={!clientReport || sendingClientReportEmail || !clientReportEmail.trim()}
                      fullWidth
                    >
                      {sendingClientReportEmail ? <CircularProgress size={20} /> : t('consultation.sendEmail')}
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Print />}
                      onClick={handlePrintClientReport}
                      disabled={!clientReport}
                      sx={{ flex: 1 }}
                    >
                      {t('consultation.print')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={() => {
                        navigator.clipboard.writeText(clientReport);
                      }}
                      disabled={!clientReport}
                      sx={{ flex: 1 }}
                    >
                      {t('consultation.copyToClipboard')}
                    </Button>
                  </Box>
                </>
              )}


              {/* Tab 2: Prescription */}
              {reportTab === 2 && (
                <>
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    value={prescription}
                    onChange={(e) => {
                      setPrescription(e.target.value);
                      handleSavePrescription();
                    }}
                    placeholder={t('consultation.prescriptionPlaceholder')}
                    sx={{
                      flex: 1,
                      mb: { xs: 1, md: 2 },
                      '& .MuiInputBase-root': {
                        height: '100%',
                        alignItems: 'flex-start',
                        fontSize: isExtensionMode ? '0.875rem' : '1rem'
                      },
                      '& textarea': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        fontSize: isExtensionMode ? '0.8125rem' : '0.875rem'
                      }
                    }}
                  />

                  <Box sx={{ mb: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('consultation.recipientEmail')}
                      value={prescriptionEmail}
                      onChange={(e) => setPrescriptionEmail(e.target.value)}
                      type="email"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={handleSendPrescriptionEmail}
                      disabled={!prescription || sendingPrescriptionEmail || !prescriptionEmail.trim()}
                      fullWidth
                    >
                      {sendingPrescriptionEmail ? <CircularProgress size={20} /> : t('consultation.sendEmail')}
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Print />}
                      onClick={handlePrintPrescription}
                      disabled={!prescription}
                      sx={{ flex: 1 }}
                    >
                      {t('consultation.print')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={() => {
                        navigator.clipboard.writeText(prescription);
                      }}
                      disabled={!prescription}
                      sx={{ flex: 1 }}
                    >
                      {t('consultation.copyToClipboard')}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Right: AI Assistant */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            p: { xs: 1.5, md: 3 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* AI Assistant Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={aiTab}
                onChange={(_, newValue) => setAiTab(newValue)}
                variant="fullWidth"
                sx={{ '& .MuiTab-root': { minWidth: 0, px: { xs: 1, sm: 2 } } }}
              >
                {[
                  { icon: <AutoAwesome fontSize="small" />, label: t('consultation.aiAssistant') },
                  { icon: <Chat fontSize="small" />, label: t('consultation.chat') },
                  { icon: <Medication fontSize="small" />, label: t('consultation.drugsTab') },
                  { icon: <LocalDining fontSize="small" />, label: t('consultation.alimentationTab') },
                ].map(({ icon, label }, i) => (
                  <Tab
                    key={i}
                    sx={{ flex: { xs: aiTab === i ? 3 : 1, sm: 1 } }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {icon}
                        <Box
                          component="span"
                          sx={{ display: { xs: aiTab === i ? 'inline' : 'none', sm: 'inline' } }}
                        >
                          {label}
                        </Box>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            {/* Content Container - Fixed Height for Stability */}
            <Box sx={{ height: '700px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* Tab 0: AI Suggestions */}
              {aiTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateSuggestions}
                    disabled={generatingSuggestions || !reportText}
                    sx={{ mb: 2 }}
                    fullWidth
                  >
                    {generatingSuggestions ? <CircularProgress size={20} /> : t('consultation.generateSuggestions')}
                  </Button>

                  {/* Suggestions List */}
                  <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1, p: 2 }}>
                    {consultation.ai_suggestions ? (
                      <>
                        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                          {t('consultation.aiSuggestions')}
                        </Typography>
                        <AISuggestionsDisplay
                          consultationId={consultation.id}
                          currentReport={reportText}
                          suggestions={consultation.ai_suggestions}
                          rag_citations={consultation.ai_suggestions?.rag_citations}
                          onNavigateToDrug={handleNavigateToDrug}
                          onSearchByMolecule={handleSearchByMolecule}
                        />
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
                        {t('consultation.noSuggestionsYet')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Tab 1: Chat */}
              {aiTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('consultation.chat')}
                  </Typography>

                  <Box sx={{ flex: 1, overflowY: 'auto', mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    {chatMessages.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('consultation.noChatMessages')}
                      </Typography>
                    ) : (
                      <>
                        {chatMessages.map((msg, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              mb: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <Box sx={{
                              maxWidth: '85%',
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                              color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                              border: msg.role === 'assistant' ? 1 : 0,
                              borderColor: 'divider',
                              boxShadow: 1
                            }}>
                              {msg.role === 'assistant' ? (
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    p: ({ node, ...props }) => <Typography variant="body2" sx={{ mb: 1, '&:last-child': { mb: 0 }, fontSize: isExtensionMode ? '0.8125rem' : '0.875rem' }}>{props.children}</Typography>,
                                    ul: ({ node, ...props }) => <Box component="ul" sx={{ pl: 2, my: 0.5 }}>{props.children}</Box>,
                                    ol: ({ node, ...props }) => <Box component="ol" sx={{ pl: 2, my: 0.5 }}>{props.children}</Box>,
                                    li: ({ node, ...props }) => <Box component="li" sx={{ typography: 'body2', fontSize: isExtensionMode ? '0.8125rem' : '0.875rem' }}>{props.children}</Box>,
                                    table: ({ node, ...props }) => (
                                      <Box sx={{ overflowX: 'auto', my: 1 }}>
                                        <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: isExtensionMode ? '0.75rem' : '0.8125rem' }}>{props.children}</Box>
                                      </Box>
                                    ),
                                    thead: ({ node, ...props }) => <Box component="thead" sx={{ bgcolor: 'action.hover' }}>{props.children}</Box>,
                                    tbody: ({ node, ...props }) => <Box component="tbody">{props.children}</Box>,
                                    tr: ({ node, ...props }) => <Box component="tr" sx={{ '&:nth-of-type(even)': { bgcolor: 'action.selected' } }}>{props.children}</Box>,
                                    th: ({ node, ...props }) => <Box component="th" sx={{ border: 1, borderColor: 'divider', px: 1, py: 0.5, fontWeight: 'bold', textAlign: 'left', whiteSpace: 'nowrap' }}>{props.children}</Box>,
                                    td: ({ node, ...props }) => <Box component="td" sx={{ border: 1, borderColor: 'divider', px: 1, py: 0.5 }}>{props.children}</Box>,
                                    a: ({ node, ...props }) => {
                                      // Handle citation links
                                      if (props.href?.startsWith('#citation-')) {
                                        const index = parseInt(props.href.replace('#citation-', '')) - 1;
                                        const citation = msg.citations?.[index];

                                        if (citation) {
                                          const isMed = citation.metadata?.type === 'medication';
                                          return (
                                            <Link
                                              component="button"
                                              onClick={(e) => handleChatCitationClick(e, citation)}
                                              sx={{
                                                fontSize: '0.75em',
                                                verticalAlign: 'super',
                                                textDecoration: 'none',
                                                fontWeight: 'bold',
                                                mx: 0.2,
                                                color: isMed ? 'secondary.main' : 'primary.main',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.2
                                              }}
                                            >
                                              [{index + 1}]
                                              {isMed && <Medication sx={{ fontSize: 10 }} />}
                                            </Link>
                                          );
                                        } else {
                                          // Citation missing/invalid index - render text only, no link
                                          return <span style={{ fontSize: '0.75em', verticalAlign: 'super', color: 'gray' }}>[{index + 1}]</span>;
                                        }
                                      }
                                      // External links - open in new tab
                                      return <a {...props} target="_blank" rel="noopener noreferrer" />;
                                    }
                                  }}
                                >
                                  {msg.content.replace(/\[(\d+(?:,\s*\d+)*)\]/g, (match, nums) => {
                                    const parts = nums.split(/,\s*/);
                                    return parts.map((n: string) => `[${n}](#citation-${n})`).join(', ');
                                  })}
                                </ReactMarkdown>
                              ) : (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                                  {msg.content}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
                              {msg.role === 'user' ? t('consultation.you') : t('consultation.assistant')}
                            </Typography>
                          </Box>
                        ))}

                        {/* Loading Indicator - only show before streaming starts */}
                        {sendingMessage && !isStreaming && (
                          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Box sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <CircularProgress size={16} />
                              <Typography variant="caption" color="text.secondary">
                                {t('consultation.thinking')}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <div ref={chatEndRef} />
                      </>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('consultation.askQuestion')}
                      value={chatQuestion}
                      onChange={(e) => setChatQuestion(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendChatMessage();
                        }
                      }}
                      disabled={sendingMessage}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSendChatMessage}
                      disabled={!chatQuestion.trim() || sendingMessage}
                      sx={{ minWidth: '40px', padding: '10px 12px', minHeight: '35px', transform: 'translateY(-5px)' }}
                    >
                      {sendingMessage ? <CircularProgress size={20} /> : <Send />}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Tab 2: Drugs */}
              {aiTab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                  {/* Drug Search */}
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <DrugSearch
                      onSelectDrug={setSelectedDrugId}
                      extractedDrugs={extractedDrugs}
                      species={animalInfoDraft.patient_species || undefined}
                      moleculeQuery={drugMoleculeQuery}
                    />
                  </Box>

                  {/* Drug Details */}
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <DrugDetailsCard
                      drugId={selectedDrugId}
                      weight={animalInfoDraft.patient_weight}
                      species={animalInfoDraft.patient_species || undefined}
                      isSubmitting={generatingPrescription}
                      onAddToPrescription={async (drugId, scenarioLabel) => {
                        setGeneratingPrescription(true);
                        setError(null);
                        try {
                          const response = await consultationService.generatePrescriptionItem({
                            drug_id: drugId,
                            scenario_label: scenarioLabel,
                            patient_weight: animalInfoDraft.patient_weight,
                            patient_species: animalInfoDraft.patient_species || undefined,
                            clinical_report: reportText,
                            current_prescription: prescription
                          });

                          setPrescription(response.prescription_text);
                          setReportTab(2); // Switch to prescription tab
                        } catch (err: any) {
                          console.error("Failed to add drug to prescription", err);
                          setError(err.response?.data?.detail || t('consultation.prescriptionError'));
                        } finally {
                          setGeneratingPrescription(false);
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Tab 3: Alimentation */}
              {aiTab === 3 && (
                <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5 }}>
                  <AlimentationCalculator
                    key={consultation?.id}
                    weight={animalInfoDraft.patient_weight}
                    species={animalInfoDraft.patient_species || undefined}
                    sterilizationStatus={animalInfoDraft.sterilization_status || undefined}
                    consultationId={consultation?.id}
                    patientInfo={{
                      name: animalInfoDraft.patient_name || undefined,
                      breed: animalInfoDraft.patient_breed || undefined,
                      age: animalInfoDraft.patient_age || undefined,
                      gender: animalInfoDraft.patient_gender || undefined,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>


      <CitationPopup
        citation={activeChatCitation}
        anchorEl={chatCitationAnchorEl}
        onClose={handleCloseChatCitation}
        onNavigateToDrug={handleNavigateToDrug}
        onSearchByMolecule={handleSearchByMolecule}
      />
    </Box>
  );
}
